<template>
  <v-container>
    <PageHeader :title="pageTitle" :path="goToPath" />
    <v-card class="elevation-1">
      <v-card-text class="py-4">
        <v-form ref="exportForm" v-model="formValid">
          <v-row>
            <!-- Selector de Financiador -->
            <v-col cols="12" md="4">
              <v-select
                v-model="financiadorSeleccionadoId"
                :items="financiadores"
                item-title="financiadorNom"
                item-value="financiadorId"
                label="Financiador"
                :rules="[...rules.required]"
                variant="outlined"
                density="compact"
                clearable
                :loading="isLoadingFinanciadores"
              ></v-select>
            </v-col>

            <!-- Fecha Desde -->
            <v-col cols="12" md="4">
              <DatePicker
                @selectFecha="selectedDateFrom = $event"
                label="Fecha desde"
                :minDate="minDate"
                :clearable="true"
                :dateMenu="menuFechaDesde"
                :defaultDate="fechaDesde"
                :rules="selectedDateFrom && selectedDateTo ? [...rules.validDateFormat, rules.validDateRange(selectedDateFrom, selectedDateTo)] : selectedDateFrom ? [...rules.validDateFormat] : []"
              />
            </v-col>

            <!-- Fecha Hasta -->
            <v-col cols="12" md="4">
              <DatePicker
                @selectFecha="selectedDateTo = $event"
                label="Fecha hasta"
                :minDate="minDate"
                :clearable="true"
                :dateMenu="menuFechaHasta"
                :defaultDate="fechaHasta"
                :rules="selectedDateFrom && selectedDateTo ? [...rules.validDateFormat, rules.validDateRange(selectedDateFrom, selectedDateTo)] : selectedDateTo ? [...rules.validDateFormat] : []"
              />
            </v-col>
          </v-row>
          <!-- Botón de exportación -->
          <v-row>
            <v-col cols="12" class="text-center">
              <v-btn
                color="primary"
                size="large"
                variant="flat"
                @click="exportarPrescripciones"
                :disabled="!puedeExportar || isExportando"
                :loading="isExportando"
                id="v-btn-exportar-prescripciones"
              >
                <v-icon start>{{ downloadIcon }}</v-icon>
                Exportar a Excel
              </v-btn>
            </v-col>
          </v-row>

          <!-- Información del archivo a generar -->
          <v-row v-if="nombreArchivoGenerado">
            <v-col cols="12">
              <v-alert type="success" variant="tonal">
                <div class="text-subtitle-2">Archivo a generar:</div>
                <div class="text-mono">{{ nombreArchivoGenerado }}</div>
              </v-alert>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import PageHeader from "@/components/layout/PageHeader.vue";
import DatePicker from "@/components/shared/DatePicker.vue";
import enums from "@/utils/enums/index";
import { mapActions, mapGetters } from "vuex";
import rules from "@/utils/helpers/rules";
import helperExcel from "@/utils/helpers/importExportExcel";

export default {
  name: "ExportarConsultaPrescripcionesView",
  components: {
    PageHeader,
    DatePicker,
  },
  data() {
    return {
      pageTitle: enums.permises.EXPORTAR_CONSULTA_PRESCRIPCIONES.name,
      goToPath: enums.permises.HOME.path,
      downloadIcon: enums.icons.DOWNLOAD_ICON,
      formValid: false,
      isExportando: false,
      isLoadingFinanciadores: false,
      financiadores: [],
      financiadorSeleccionadoId: null,
      selectedFechaDesde: null,
      selectedFechaHasta: null,
      minDate: "2001",
      menuFechaDesde: false,
      menuFechaHasta: false,
      rules,
    };
  },
  computed: {
    // Variables Date nativas para el defaultDate del DatePicker, igual que en SolicitudesView
    fechaDesde() {
      if (!this.selectedFechaDesde) return null;
      // selectedFechaDesde viene como DD/MM/YYYY desde el text-field
      const [d, m, y] = (this.selectedFechaDesde || '').split('/');
      if (!d || !m || !y) return null;
      return new Date(`${y}-${m}-${d}T00:00:00`);
    },
    fechaHasta() {
      if (!this.selectedFechaHasta) return null;
      const [d, m, y] = (this.selectedFechaHasta || '').split('/');
      if (!d || !m || !y) return null;
      return new Date(`${y}-${m}-${d}T00:00:00`);
    },
    ...mapGetters({
      permisos: "menu/permisos",
      isMobile: "menu/isMobile",
    }),
    selectedDateFrom: {
      get() {
        return this.selectedFechaDesde;
      },
      set(val) {
        if (val && val.dateString) {
          this.selectedFechaDesde = val.dateString;
        } else if (typeof val === 'string') {
          this.selectedFechaDesde = val;
        } else {
          this.selectedFechaDesde = null;
        }
      }
    },
    selectedDateTo: {
      get() {
        return this.selectedFechaHasta;
      },
      set(val) {
        if (val && val.dateString) {
          this.selectedFechaHasta = val.dateString;
        } else if (typeof val === 'string') {
          this.selectedFechaHasta = val;
        } else {
          this.selectedFechaHasta = null;
        }
      }
    },
    /**
     * Obtiene el financiador seleccionado basado en el ID
     * @returns {Object|null} Financiador seleccionado o null
     */
    financiadorSeleccionado() {
      if (!this.financiadorSeleccionadoId) return null;
      return this.financiadores.find(
        (f) => f.financiadorId === this.financiadorSeleccionadoId
      );
    },
    /**
     * Verifica si el botón de exportación debe estar habilitado
     * @returns {boolean} True si se puede exportar
     */
    puedeExportar() {
      // Verificar que todos los campos tengan valor
      if (!this.financiadorSeleccionadoId || !this.selectedFechaDesde || !this.selectedFechaHasta) {
        return false;
      }
      
      // Verificar que el rango de fechas sea válido
      const validRange = this.rules.validDateRange(this.selectedFechaDesde, this.selectedFechaHasta);
      if (validRange !== true) {
        return false;
      }
      
      return true;
    },
    /**
     * Genera el nombre del archivo de exportación
     * @returns {string|null} Nombre del archivo o null si no está completo
     */
    nombreArchivoGenerado() {
      if (!this.financiadorSeleccionado || 
          !this.selectedFechaDesde || 
          !this.selectedFechaHasta) {
        return null;
      }
      
      const nombreFinanciador = this.limpiarNombreArchivo(this.financiadorSeleccionado.financiadorNom);
      const fechaDesde = this.formatDateForFileName(this.selectedFechaDesde);
      const fechaHasta = this.formatDateForFileName(this.selectedFechaHasta);
      
      return `${nombreFinanciador}_${fechaDesde}_${fechaHasta}_Prescripciones.xlsx`;
    },
  },
  async created() {
    await this.cargarFinanciadores();
    this.$store.dispatch("user/changeAppLoading", {
      status: false,
      text: "",
    });
  },
  methods: {
    ...mapActions({
      getFinanciadores: "financiador/getFinanciadores",
      setAlert: "user/setAlert",
        getPrescripcionesPorFinanciadorYFecha: "solicitud/getPrescripcionesPorFinanciadorYFecha",
    }),
    /**
     * Carga la lista de financiadores disponibles
     */
    async cargarFinanciadores() {
      this.isLoadingFinanciadores = true;
      try {
        const response = await this.getFinanciadores();
        this.financiadores = response || [];
      } catch (error) {
        console.error("Error al cargar financiadores:", error);
        this.setAlert({ 
          type: "error", 
          message: "Error al cargar la lista de financiadores" 
        });
      } finally {
        this.isLoadingFinanciadores = false;
      }
    },
    /**
     * Formatea una fecha para usar en nombre de archivo (YYYYMMDD)
     * @param {string} dateString - Fecha en formato YYYY-MM-DD
     * @returns {string} Fecha formateada como YYYYMMDD
     */
    formatDateForFileName(dateString) {
      if (!dateString) return "";
      
      // El DatePicker devuelve la fecha en formato DD/MM/YYYY
      // Convertir a YYYYMMDD
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const day = parts[0].padStart(2, '0');
        const month = parts[1].padStart(2, '0');
        const year = parts[2];
        return `${year}${month}${day}`;
      }
      
      return "";
    },
    /**
     * Limpia el nombre del financiador para usar en nombre de archivo
     * @param {string} nombre - Nombre original del financiador
     * @returns {string} Nombre limpio para archivo
     */
    limpiarNombreArchivo(nombre) {
      if (!nombre) return "Financiador";
      
      return nombre
        .replace(/[^a-zA-Z0-9\s]/g, "") // Remover caracteres especiales
        .replace(/\s+/g, "_") // Reemplazar espacios con guiones bajos
        .trim();
    },
    /**
     * Valida los filtros antes de exportar
     * @returns {boolean} True si todos los filtros son válidos
     */
    validarFiltrosExportacion() {
      // Validar usando la computed property
      if (!this.puedeExportar) {
        this.setAlert({ type: "warning", message: "Complete todos los campos requeridos correctamente" });
        return false;
      }
      
      return true;
    },
    /**
     * Exporta las prescripciones a Excel según los filtros seleccionados
     */
    async exportarPrescripciones() {
      if (!this.validarFiltrosExportacion()) return;
      
      this.isExportando = true;
      
      try {
        // Llamar acción del store
        const data = await this.getPrescripcionesPorFinanciadorYFecha({
          financiadorId: this.financiadorSeleccionadoId,
          fechaDesde: this.convertToIso(this.selectedFechaDesde),
          fechaHasta: this.convertToIso(this.selectedFechaHasta),
        });

        // Validar que existan registros para exportar
        if (!data || data.length === 0) {
          this.setAlert({ 
            type: "info", 
            message: "No se encontraron prescripciones para los filtros seleccionados." 
          });
          return;
        }

        // Transformar data usando el mismo formato que SolicitudesView
        const tipoColumna = "Items"; // Para esta exportación siempre será "Items"
        
        const filas = (data || []).map(x => {
          // Función helper para formatear items prescriptos (igual que SolicitudesView)
          const formatearItems = (items, tipo) => {
            if (!Array.isArray(items) || items.length === 0) {
              return "";
            }

            return items
              .map((item) => {
                let nombre, cantidad;
                
                if (tipo === 'medicamentos') {
                  nombre = item.regNom;
                  cantidad = item.cantidad;
                } else if (tipo === 'dispositivos') {
                  nombre = item.dispositivo;
                  cantidad = 1;
                } else if (tipo === 'estudios') {
                  nombre = item.estudio;
                  cantidad = item.cantidad;
                }

                const nombreCorto = `● ${(nombre || 'Sin nombre')}`.trim();
                const cantidadStr = cantidad || 1;
                return `${nombreCorto} / (${cantidadStr})`;
              })
              .join("\n");
          };

          // Determinar configuración según tipo de solicitud
          const tipoSolicitudId = x.tipoSolicitud?.id;
          const config = {
            1: { // Medicamentos
              prescriptos: 'medicamentosPreescriptos',
              tipo: 'medicamentos',
            },
            2: { // Dispositivos
              prescriptos: 'dispositivosPreescriptos',
              tipo: 'dispositivos',
            },
            3: { // Estudios
              prescriptos: 'estudiosPreescriptos',
              tipo: 'estudios',
            }
          }[tipoSolicitudId] || {
            prescriptos: 'medicamentosPreescriptos',
            tipo: 'medicamentos',
          };

          const prescriptosFormatted = formatearItems(x[config.prescriptos], config.tipo);
          
          // Formatear fecha para exportación
          const formatFechaParaExport = (fecha) => {
            if (!fecha) return '';
            if (typeof fecha === 'string') {
              const re = /^\d{2}\/\d{2}\/\d{4}$/;
              if (re.test(fecha)) return fecha;
              const d = new Date(fecha);
              if (!isNaN(d.getTime())) return this.formatDateForDisplay(d);
              return fecha;
            }
            if (fecha instanceof Date) return this.formatDateForDisplay(fecha);
            try {
              const d = new Date(fecha);
              if (!isNaN(d.getTime())) return this.formatDateForDisplay(d);
            } catch (e) {
              // Ignorar errores de conversión de fecha
            }
            return '';
          };

          // Extraer CUIRs de items (simplificado)
          const extractCuirs = (solicitud) => {
            const cuirs = [];
            ['medicamentosPreescriptos', 'dispositivosPreescriptos', 'estudiosPreescriptos'].forEach(prop => {
              if (Array.isArray(solicitud[prop])) {
                solicitud[prop].forEach(item => {
                  if (item.cuir) cuirs.push(item.cuir);
                });
              }
            });
            return cuirs;
          };

                 return {
                   ["N° solicitud"]: x.numero,
                   ["Tipo prescripción"]: x.tipoSolicitud?.value || "",
                   ["Fecha creación"]: formatFechaParaExport(x.fechaCreacion),
                   ["Fecha prescripción"]: formatFechaParaExport(x.fechaEmision),
                   ["Emisor"]: x.emisor?.value || "",
                   ["Financiador"]: x.financiador?.value || "",
                   ["Estado"]: x.estado,
                   [`${tipoColumna} prescriptos / (Cantidad)`]: prescriptosFormatted,
                   ["N° afiliado"]: x.nroAfi,
                   ["Afiliado"]: x.apeNomAfi,
                   ["Email"]: x.afiliadoContactInfo?.email || "",
                   ["Teléfono"]: x.afiliadoContactInfo?.telefono || "",
                   ["Médico Nombre"]: x.medicoPrescriptor ? `${x.medicoPrescriptor.apellido || ""} ${x.medicoPrescriptor.nombre || ""}`.trim() : "",
                   ["Médico Provincia"]: x.medicoPrescriptor?.matricula?.provincia || "",
                   ["Médico Matrícula"]: x.medicoPrescriptor?.matricula?.numero || x.nroMatricula || "",
                   ["Médico Código REFEPS"]: x.medicoPrescriptor?.codigoRefeps || "",
                   ["CUIR"]: extractCuirs(x).join(', ') || "",
                 };
        });

        // Configurar anchos específicos para columnas de ítems (igual que SolicitudesView)
        const columnWidths = {};
        if (filas.length > 0) {
          Object.keys(filas[0]).forEach(key => {
            const keyLower = key.toLowerCase();
            if (keyLower.includes("prescriptos")) {
              columnWidths[key] = 80; // Ancho específico para columnas de ítems
            }
          });
        }
        
        const exportOptions = {
          maxColumnWidth: 100,
          columnWidths: columnWidths
        };

        await helperExcel.excelExport(
          { hoja: "Recetas", filas }, 
          this.nombreArchivoGenerado.replace('.xlsx',''),
          exportOptions
        );
        this.setAlert({ type: "success", message: `Archivo generado exitosamente.` });
        
      } catch (error) {
        console.error("Error al exportar prescripciones:", error);
        this.setAlert({ 
          type: "error", 
          message: "Error al exportar las prescripciones. Intente nuevamente." 
        });
      } finally {
        this.isExportando = false;
      }
    },
    convertToIso(ddmmyyyy) {
      // Convierte DD/MM/YYYY -> YYYY-MM-DD
      if (!ddmmyyyy) return null;
      const [d, m, y] = ddmmyyyy.split('/');
      if (!d || !m || !y) return null;
      return `${y}-${m}-${d}`;
    },
    formatDateForDisplay(date) {
      // Helper para formatear fechas igual que SolicitudesView
      if (!date) return '';
      const d = new Date(date);
      if (isNaN(d.getTime())) return '';
      
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      
      return `${day}/${month}/${year}`;
    }
  },
};
</script>

<style lang="scss" scoped>
.text-mono {
  font-family: monospace;
  font-size: 0.9em;
}

.v-alert {
  margin-bottom: 16px;
}

.v-form {
  max-width: 100%;
}

@media screen and (max-width: 600px) {
  .text-center {
    text-align: left !important;
  }
}
</style>
